import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import axiosInstance from '@/setup/axios/axiosInstance';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';

import DataTableModule from '@/__core/layout/module/DataTableModule';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';

import { InventoryStockBalanceRow } from '../types';

export type StockBalanceListHandle = {
  requestRefresh: (options?: { showSuccessMessage?: boolean }) => void;
};

interface StockBalanceListProps {
  companyId?: string;
  searchQuery?: string;
  stockState?: string;
  onSelectRow?: (row: InventoryStockBalanceRow) => void;
  onFetchError?: (message: string) => void;
  onRefreshSuccess?: () => void;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const resolveListCompanyId = (companyId?: string) => {
  const resolved = (companyId || getCurrentCompanyId() || '').trim();
  if (!resolved || resolved === 'all' || resolved === 'global' || !UUID_PATTERN.test(resolved)) {
    return undefined;
  }
  return resolved;
};

interface ApiStockBalanceItem {
  id: string;
  item_id?: string;
  item_code?: string;
  item_name?: string;
  store_id?: string;
  store_code?: string;
  store_name?: string;
  company_id?: string;
  company_name?: string;
  lot_no?: string;
  bin_location?: string;
  on_hand_qty?: string | number;
  allocated_qty?: string | number;
  available_qty?: string | number;
  last_price?: string | number;
  updated_at?: string;
  last_received_at?: string;
  last_movement_at?: string;
}

const mapApiStockBalance = (row: ApiStockBalanceItem): InventoryStockBalanceRow => ({
  id: row.id,
  item_id: row.item_id || '',
  item_code: row.item_code || '',
  item_name: row.item_name || '',
  store_id: row.store_id || '',
  store_code: row.store_code || '',
  store_name: row.store_name || '',
  company_id: row.company_id || '',
  company_name: row.company_name || '',
  lot_no: row.lot_no || '',
  bin_location: row.bin_location || '',
  on_hand_qty: Number(row.on_hand_qty || 0),
  allocated_qty: Number(row.allocated_qty || 0),
  available_qty: Number(row.available_qty || 0),
  last_price: Number(row.last_price || 0),
  updated_at: row.updated_at || '',
  last_received_at: row.last_received_at || '',
  last_movement_at: row.last_movement_at || '',
  note: '',
});

const balanceTextColor = (row: InventoryStockBalanceRow) => {
  if (row.available_qty < 0) return '#dc2626';
  if (row.available_qty === 0) return '#d97706';
  return '#16a34a';
};

const balanceLabel = (row: InventoryStockBalanceRow) => {
  if (row.available_qty < 0) {
    return 'Thiếu khả dụng';
  }
  if (row.available_qty === 0) {
    return 'Hết khả dụng';
  }
  if (row.allocated_qty > 0) {
    return 'Có giữ chỗ';
  }
  return 'Còn khả dụng';
};

const StockBalanceList = forwardRef<StockBalanceListHandle, StockBalanceListProps>(function StockBalanceList(
  { companyId, searchQuery = '', stockState = 'all', onSelectRow, onFetchError, onRefreshSuccess },
  ref
) {
  const [rows, setRows] = useState<InventoryStockBalanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [showSuccessOnLoad, setShowSuccessOnLoad] = useState(false);

  const resolvedCompanyId = useMemo(() => resolveListCompanyId(companyId), [companyId]);

  useEffect(() => {
    let active = true;

    const fetchStockBalances = async () => {
      setLoading(true);
      try {
        const query = new URLSearchParams();
        if (resolvedCompanyId) {
          query.append('company', resolvedCompanyId);
        }
        query.append('page_size', '200');

        const qs = query.toString();
        const response = await axiosInstance.get<{ results?: ApiStockBalanceItem[] } | ApiStockBalanceItem[]>(
          `/inventory/stock-balances/${qs ? `?${qs}` : ''}`
        );

        if (!active) return;

        const data = response.data;
        const payload = Array.isArray(data) ? data : (data.results || []);
        setRows(payload.map(mapApiStockBalance));

        if (showSuccessOnLoad) {
          onRefreshSuccess?.();
          setShowSuccessOnLoad(false);
        }
      } catch (error: any) {
        if (!active) return;
        setRows([]);
        setShowSuccessOnLoad(false);
        onFetchError?.(error?.message || 'Không thể tải dữ liệu tồn kho.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    const timeout = window.setTimeout(() => {
      void fetchStockBalances();
    }, 200);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [resolvedCompanyId, reloadTick]);

  useImperativeHandle(
    ref,
    () => ({
      requestRefresh: (options) => {
        if (options?.showSuccessMessage) {
          setShowSuccessOnLoad(true);
        }
        setReloadTick((current) => current + 1);
      },
    }),
    []
  );

  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        const matchesSearch =
          !searchQuery ||
          `${row.item_code} ${row.item_name} ${row.store_code} ${row.store_name} ${row.lot_no} ${row.bin_location} ${row.note}`
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        const matchesState =
          stockState === 'all' ||
          (stockState === 'available' && row.available_qty > 0) ||
          (stockState === 'allocated' && row.allocated_qty > 0) ||
          (stockState === 'shortage' && row.available_qty <= 0);
        return matchesSearch && matchesState;
      }),
    [rows, searchQuery, stockState]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!filteredRows.length) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, height: 220 }}>
        <Typography variant='body1' color='text.secondary'>Không có số dư tồn kho</Typography>
        <Typography variant='body2' color='text.secondary'>Thử thay đổi bộ lọc hoặc làm mới danh sách.</Typography>
      </Box>
    );
  }

  const columns: Array<{
    id: string;
    label: string;
    width?: string;
    align?: 'left' | 'center' | 'right';
    render?: (_: unknown, row: InventoryStockBalanceRow) => React.ReactNode;
  }> = [
    { id: 'item_code', label: 'Mã vật tư', width: '140px', render: (_: unknown, row: InventoryStockBalanceRow) => <Typography sx={{ fontWeight: 600 }}>{row.item_code}</Typography> },
    { id: 'item_name', label: 'Tên vật tư', width: '240px' },
    { id: 'store_name', label: 'Kho', width: '180px', render: (_: unknown, row: InventoryStockBalanceRow) => row.store_name || '-' },
    { id: 'lot_no', label: 'Lô', width: '140px', render: (_: unknown, row: InventoryStockBalanceRow) => row.lot_no || '-' },
    { id: 'bin_location', label: 'Vị trí kệ', width: '140px', render: (_: unknown, row: InventoryStockBalanceRow) => row.bin_location || '-' },
    { id: 'on_hand_qty', label: 'Tồn thực tế', width: '120px', align: 'right' },
    { id: 'allocated_qty', label: 'Đã gán', width: '110px', align: 'right' },
    { id: 'available_qty', label: 'Khả dụng', width: '110px', align: 'right' },
    { id: 'last_price', label: 'Giá gần nhất', width: '140px', align: 'right', render: (_: unknown, row: InventoryStockBalanceRow) => row.last_price.toLocaleString('vi-VN') },
    { id: 'balance_state', label: 'Tình trạng', width: '140px', render: (_: unknown, row: InventoryStockBalanceRow) => <Typography sx={{ color: balanceTextColor(row), fontWeight: 600, fontSize: '0.875rem' }}>{balanceLabel(row)}</Typography> },
  ];

  return (
    <Box sx={sharedListStyles.container}>
      <DataTableModule
        data={filteredRows}
        columns={columns}
        leftFilterWidth={0}
        onRowSelect={() => undefined}
        onSelectAll={() => undefined}
        onRowClick={(row) => onSelectRow?.(row as InventoryStockBalanceRow)}
        getRowId={(row) => (row as InventoryStockBalanceRow).id}
        isRowSelected={() => false}
        areAllRowsSelected={false}
        areSomeRowsSelected={false}
        showCheckbox={false}
      />
    </Box>
  );
});

export default StockBalanceList;
